'use client';

import { ReactNode } from 'react';
import { css } from '@bts-web/utils-style-engine';
import {
  intlFiatValueFormatting,
  MaximumFractionDigits,
} from '@bts-web/utils-formatting';
import { OpenDrawerFunction } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { useIsBelowScreenWidth } from '../../../layouts/utils/useIsBelowScreenWidth';
import {
  SmallIconMapper,
  TradeAssetSelectorTranslations,
} from '../../ChangeCurrencyOrSwap/changeCurrencyUtils';
import { BankAccountDetailsDrawer } from './BankAccountDetailsDrawer';
import { FiatWalletDetailsDrawer } from './FiatWalletDetailsDrawer';
import { PaymentMethodRow, sectionLabelStyles } from './PaymentMethodRow';

export const DepositNeededCell = ({
  walletAmount,
  depositAmount,
  currency,
  locale,
  appLogo,
  accountNumber,
  bankAccountHolder,
  bankName,
  openDrawer,
  translations,
  hasWalletBalance,
}: {
  walletAmount: string;
  depositAmount: string;
  locale: string;
  currency: string;
  appLogo: ReactNode;
  accountNumber: string;
  bankAccountHolder: string;
  bankName: string;
  openDrawer: OpenDrawerFunction;
  translations: TradeAssetSelectorTranslations;
  hasWalletBalance: boolean;
}) => {
  const isMobile = useIsBelowScreenWidth(1024);

  const walletFormatted = intlFiatValueFormatting(walletAmount, {
    locale,
    currency,
    fractionDigits: MaximumFractionDigits.FIAT,
  });

  const depositFormatted = intlFiatValueFormatting(depositAmount, {
    locale,
    currency,
    fractionDigits: MaximumFractionDigits.FIAT,
  });

  const handleTransferDetails = () => {
    openDrawer(BankAccountDetailsDrawer, {
      accountNumber,
      bankAccountHolder,
      bankName,
      position: isMobile ? 'bottom' : 'right',
    });
  };

  const handleWalletDetails = () => {
    openDrawer(FiatWalletDetailsDrawer, {
      currency,
      locale,
      walletAmount,
      position: isMobile ? 'bottom' : 'right',
    });
  };

  return (
    <div
      data-testid="buy-source-fiat-wallet"
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: 'extra_small',
        paddingBottom: 'medium',
        borderBottom: '1px solid',
        borderBottomColor: 'neutrals.divider',
        width: '100%',
      })}
    >
      <span className={sectionLabelStyles}>
        {hasWalletBalance
          ? translations.paymentMethods
          : translations.paymentMethod}
      </span>

      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 'small',
          width: '100%',
        })}
      >
        {hasWalletBalance && (
          <PaymentMethodRow
            icon={SmallIconMapper[currency as keyof typeof SmallIconMapper]}
            name={translations.fiatWalletLabel}
            amount={walletFormatted}
            onClick={handleWalletDetails}
          />
        )}
        <PaymentMethodRow
          icon={appLogo}
          name={translations.onlineTransfer}
          amount={depositFormatted}
          onClick={handleTransferDetails}
        />
      </div>
    </div>
  );
};
